import React, { useEffect, useRef, useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleContextConsumer, CollapsibleHeading } from '$partials/base';
import { ArrowDownIcon, ArrowUpIcon } from '$partials/icon';
import { Image } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').Accordion}
 */
const Accordion = ({
    label,
    title,
    image,
    items,
    video
}) => {
    const [videoHeight, setVideoHeight] = useState(undefined);
    const [videoWidth, setVideoWidth] = useState(undefined);
    const videoRef = useRef();

    const handleVideoLoad = () => {
        setVideoHeight(videoRef.current.videoHeight);
        setVideoWidth(videoRef.current.videoWidth);
    };

    const isLandscape = () => {
        if (video) {
            return videoWidth > videoHeight;
        }
        return image.width > image.height;

    };

    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            video.addEventListener('loadedmetadata', handleVideoLoad, { passive: true });

            window.setTimeout(() => {
                handleVideoLoad();
            }, 100);
        }

        return () => {
            if (video) {
                video.removeEventListener('loadedmetadata', handleVideoLoad, { passive: true });
            }
        };
    }, []);

    // render
    return (
        <>
            <div className='absolute trunk-y-bleed translate-yn-100 zn1'>
                <div className='bg-peach height-7' />
            </div>
            <div className='bg-peach trunk-full-width'>
                <div className='container col-12 flex flex-wrap sm-flex-no-wrap flex-col-reverse sm-flex-row justify-between pt6 pb9 md-py9'>
                    <div className='col-12 sm-col-6 md-col-5'>
                        <div className='col-12 md-col-10'>
                            {label && <p className='type-style-5 mb3'>{label}</p>}
                            <h1 className='type-style-1 sans-serif'>{title}</h1>
                        </div>
                        <div className='col-12 mt5 sm-mt3 sm-pr7 md-pr0'>
                            {items.map((item, idx) => {
                                return (
                                    <Collapsible
                                        className={`${idx < items.length - 1 ? 'border-bottom black' : '' }`}
                                        id='directory-address-accordion'
                                        isOpened={false}
                                        key={`accordion-collapsible-${idx}`}>
                                        <CollapsibleHeading className='form-field-reset link-reset width-100'>
                                            <CollapsibleContextConsumer>
                                                {({ isOpened }) => (
                                                    <div
                                                        className={`align-left inline-flex width-100 justify-between py5 md-py5 items-center bg-none type-style-5 cursor-pointer${!isOpened && idx === items.length - 1 ? ' border-bottom black' : ' '}`}>
                                                        {item.label}
                                                        <span
                                                            className='font-0 line-height-0 bg-white black button border-radius-100px p3 md-p5'>
                                                            {isOpened ? <ArrowUpIcon/> : <ArrowDownIcon/>}
                                                        </span>
                                                    </div>
                                                )}
                                            </CollapsibleContextConsumer>
                                        </CollapsibleHeading>
                                        <CollapsibleContent className='clearfix'>
                                            <div
                                                className='rich-text type-style-6 black mt3 mb5'
                                                dangerouslySetInnerHTML={{__html: item.body}}/>
                                        </CollapsibleContent>
                                    </Collapsible>
                                );
                            })}
                        </div>
                    </div>
                    {(image || video) && (
                        <div className={`col-12 mb6 sm-mb0 sm-pl9 md-pl0 md-pr12${isLandscape() ? ' sm-col-7 md-col-6' : ' sm-col-6 md-col-5'}`}>
                            <div style={{maxWidth: isLandscape() ? '100%' : '500px'}}>
                                {video ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        src={video.videoUrl}/>
                                ) : (
                                    <Image {...image}/>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='absolute trunk-y-bleed zn1'>
                <div className='bg-peach height-7'/>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Accordion;
